import { Text } from '@belong/ui';
import { IconRightCaret } from '@belong/icons';
import { clsx } from 'clsx';
import type { FailedPaymentsHomeGroup } from '../failed-payments-utils';
import {
  formatAddressWithoutStateAndZip,
  formatNegativeFailedPaymentsAmount,
  getFailedPaymentsCount,
  getFailedPaymentsTotal,
} from '../failed-payments-utils';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';

type SelectHomeStepProps = {
  homeGroups: FailedPaymentsHomeGroup[];
  resolvedHomeKeys: string[];
  onSelectHome: (key: string) => void;
};

function getOutstandingLabel(count: number) {
  if (count === 1) {
    return FAILED_PAYMENTS_STRINGS['select_home.outstanding_balance'];
  }
  return FAILED_PAYMENTS_STRINGS['select_home.outstanding_balances'].replace(
    '{count}',
    String(count),
  );
}

export function SelectHomeStep({
  homeGroups,
  resolvedHomeKeys,
  onSelectHome,
}: SelectHomeStepProps) {
  const resolvedCount = resolvedHomeKeys.length;
  const remainingCount = homeGroups.length - resolvedCount;
  const hasResolvedHome = resolvedCount > 0;

  const title = hasResolvedHome
    ? FAILED_PAYMENTS_STRINGS['select_home.next_title']
    : FAILED_PAYMENTS_STRINGS['select_home.title'];
  const subtitle = hasResolvedHome
    ? remainingCount === 1
      ? FAILED_PAYMENTS_STRINGS['select_home.next_subtitle_singular']
      : FAILED_PAYMENTS_STRINGS['select_home.next_subtitle'].replace(
          '{count}',
          String(remainingCount),
        )
    : null;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs">
        <Text
          variant="h2"
          fontWeight="semibold"
          className="whitespace-pre-line px-2sm text-center md:px-0"
        >
          {title}
        </Text>
        {subtitle ? (
          <Text variant="p1" className="text-center text-dark-gray">
            {subtitle}
          </Text>
        ) : null}
      </div>

      <div className="flex flex-col gap-sm">
        {homeGroups.map((homeGroup) => {
          const isResolved = resolvedHomeKeys.includes(homeGroup.key);
          const count =
            getFailedPaymentsCount(homeGroup.failedPayments) ||
            homeGroup.failedPayments.length;
          const total = getFailedPaymentsTotal(homeGroup.failedPayments);

          const content = (
            <>
              <span className="flex min-w-0 flex-1 flex-col items-start gap-2xs text-left">
                <Text
                  fontWeight="semibold"
                  className={clsx(
                    'w-full truncate',
                    isResolved ? 'text-dark-gray' : 'text-navy',
                  )}
                >
                  {formatAddressWithoutStateAndZip(homeGroup.homeAddress) ?? ''}
                </Text>
                <Text
                  variant="p1"
                  fontWeight="semibold"
                  className={isResolved ? 'text-dark-gray' : 'text-red'}
                >
                  {getOutstandingLabel(count)}
                </Text>
              </span>
              <span className="flex shrink-0 items-center gap-xs">
                <span className="flex flex-col items-end gap-2xs">
                  <Text
                    fontWeight="semibold"
                    className={clsx(
                      'text-right',
                      isResolved ? 'text-dark-gray' : 'text-navy',
                    )}
                  >
                    {formatNegativeFailedPaymentsAmount(total)}
                  </Text>
                  {isResolved ? (
                    <Text
                      variant="p1"
                      fontWeight="semibold"
                      className="text-green"
                    >
                      {FAILED_PAYMENTS_STRINGS['select_home.resolved']}
                    </Text>
                  ) : null}
                </span>
                {isResolved ? (
                  <span className="w-[12px] shrink-0" />
                ) : (
                  <IconRightCaret
                    width={12}
                    height={22}
                    className="text-navy"
                  />
                )}
              </span>
            </>
          );

          if (isResolved) {
            return (
              <div
                key={homeGroup.key}
                className="flex min-h-[86px] items-center justify-between gap-xs rounded bg-light-gray p-sm"
              >
                {content}
              </div>
            );
          }

          return (
            <button
              key={homeGroup.key}
              type="button"
              onClick={() => onSelectHome(homeGroup.key)}
              className="flex min-h-[86px] cursor-pointer items-center justify-between gap-xs rounded border-0 bg-white p-sm shadow"
            >
              {content}
            </button>
          );
        })}
      </div>
    </div>
  );
}
